import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Button from 'reactstrap/lib/Button';
import { useToggle } from 'site-modules/shared/hooks/use-toggle';
import { AnimatedCollapse } from 'site-modules/shared/utils/collapse-utils';
import { YearLink } from 'site-modules/core-page/components/other-years-core-6811/year-link/year-link';

const YEARS_PER_LINE = { true: 4, false: 7 };
const YEAR_LINK_HEIGHT = 32;
const ROW_GAP = 24;
const OVERFLOW_FOCUSABLE_PADDING = 10;
const COLLAPSE_BTN_GRID_SIZE = 0.7;

export function YearLinksList({
  years,
  currentYear,
  firstLinksFilter,
  a11yMoreLabel,
  className,
  linkTrackingId,
  isMobile,
}) {
  const collapseId = `year-links-${React.useId().replace(/\W/g, '')}`;
  const [isOpen, toggleOpen] = useToggle(false);

  if (!years?.length) {
    return null;
  }

  const firstLinks = years.filter(firstLinksFilter);
  const otherLinks = years.filter(year => !firstLinks.includes(year));
  const yearsPerLine = YEARS_PER_LINE[isMobile];

  const hasCollapse = !!otherLinks?.length;
  const firstRowsNum = Math.ceil((firstLinks.length + (hasCollapse ? 1 : 0)) / yearsPerLine);

  const collapseButton = (
    <Button
      size="sm"
      color="link"
      onClick={toggleOpen}
      className="collapse-year-btn small text-transform-none p-0"
      style={{ fontSize: '.75rem', textTransform: 'none', minWidth: '3rem', fontWeight: 'normal' }}
      data-tracking-id="see_other_years"
      aria-label={`${isOpen ? 'Less' : 'More'} ${a11yMoreLabel}`}
      aria-controls={collapseId}
      aria-expanded={isOpen}
    >
      {isOpen ? 'less' : 'more'}{' '}
      <i className={classnames('icon size-9', { 'icon-arrow-down3': !isOpen, 'icon-arrow-up3': isOpen })} aria-hidden />
    </Button>
  );

  return (
    <div
      className={classnames('year-links-list', className)}
      style={{
        display: 'grid',
        gridTemplateColumns: `${yearsPerLine}fr ${COLLAPSE_BTN_GRID_SIZE}fr`,
      }}
    >
      <AnimatedCollapse
        collapseHeight={`${
          firstRowsNum * YEAR_LINK_HEIGHT + (firstRowsNum - 1) * ROW_GAP + OVERFLOW_FOCUSABLE_PADDING
        }px`}
        isOpen={isOpen}
        className="overflow-hidden-focusable"
        id={collapseId}
      >
        <ul
          className={classnames('years-list-grid list-unstyled', { open: isOpen })}
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${yearsPerLine}, 1fr)`,
          }}
        >
          {firstLinks.map(({ name, url }) => (
            <YearLink
              className="d-flex"
              key={`year-${name}`}
              year={name}
              url={url}
              currentYear={currentYear}
              skipCurrentYear
              trackingId={linkTrackingId}
            />
          ))}
          {hasCollapse && (
            <li
              className={classnames({
                'd-none': isOpen,
                'd-flex justify-content-center justify-content-md-start align-items-center': !isOpen,
              })}
            >
              {collapseButton}
            </li>
          )}
          {otherLinks.map(({ name, url }, index) => (
            <YearLink
              className={classnames('collapsible-link', { 'd-flex': index || isOpen, 'd-none': !index && !isOpen })}
              key={`year-${name}`}
              year={name}
              url={url}
              currentYear={currentYear}
              skipCurrentYear
              trackingId={linkTrackingId}
              isButton
            />
          ))}
        </ul>
      </AnimatedCollapse>
      {hasCollapse && (
        <div
          className={classnames('justify-content-end align-items-start py-0_5', {
            'd-none': !isOpen,
            'd-flex': isOpen,
          })}
        >
          {collapseButton}
        </div>
      )}
    </div>
  );
}

YearLinksList.propTypes = {
  firstLinksFilter: PropTypes.func.isRequired,
  years: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      url: PropTypes.string,
    })
  ),
  currentYear: PropTypes.string,
  a11yMoreLabel: PropTypes.string,
  className: PropTypes.string,
  linkTrackingId: PropTypes.string,
  isMobile: PropTypes.bool,
};

YearLinksList.defaultProps = {
  years: null,
  currentYear: null,
  a11yMoreLabel: null,
  className: null,
  linkTrackingId: null,
  isMobile: false,
};
